import { type EntitlementsApi } from "@entitlements/api-client"
import { ActiveSubscription, convertSubscription } from "@entitlements/client"
import { type ApiModels as T } from "@entitlements/api-models"
import { entitlementsApi } from "./entitlementsApi"
import { getCurrentUser } from "../../stores/UserStore.svelte"

export class SubscriptionManager {
	api: EntitlementsApi
	currentUserId: string
	subscriptions: Array<ActiveSubscription> = $state([])

	constructor(api: EntitlementsApi, currentUserId: number) {
		this.api = api
		this.currentUserId = `${currentUserId}`
	}

	/**
	 * NULL unless actively fetching user subscriptions. Use for rendering loading states.
	 */
	loadingPromise: Promise<Array<T["Subscription"]>> = $state()

	/**
	 * Fetches and returns the customer subscriptions from the api.
	 * Saves the api request as a promise which can be leveraged to show loading states.
	 */
	private async fetchUserSubscriptions() {
		if (!this.loadingPromise) {
			this.loadingPromise = (async () => {
				const subscriptions = await this.api.fetchCustomerSubscriptions(this.currentUserId)
				return Object.values(subscriptions)
			})()

			this.loadingPromise.catch(() => {
				this.loadingPromise = null
			})
		}

		return this.loadingPromise
	}

	/**
	 * Converts the subscription object from an API response into the ActiveSubscription type
	 */
	private convertSubscriptions(subscriptions: Array<T["Subscription"]>): Array<ActiveSubscription> {
		return subscriptions.map((sub) => convertSubscription(sub))
	}

	/**
	 * Refreshes the user's subscriptions by fetching them and updating this.subscriptions
	 * @param force if true, don't await the previous user subscription fetch to resolve`
	 */
	async refreshUserSubscriptions(force = false) {
		if (force) this.loadingPromise = null
		const subscriptions = await this.fetchUserSubscriptions()
		this.subscriptions = this.convertSubscriptions(subscriptions)
	}
}

let subscriptionManager: SubscriptionManager
export function getSubscriptionManager() {
	if (!subscriptionManager)
		subscriptionManager = new SubscriptionManager(entitlementsApi, getCurrentUser().id)

	return subscriptionManager
}
